import { useState, useMemo } from 'react';
import { C, AIRVIEW_BASE } from '../config';
import { Pill, UsageDots } from './Atoms';

function PatientCard({ patient, listType, onContact, hasLog }) {
  const daysLeft = Math.max(0, 30 - patient.days);
  const useRate  = patient.days > 0 ? Math.round((patient.days4hr / patient.days) * 100) : 0;
  const urgColor = listType==="A" ? (patient.days4hr===0 ? C.red : daysLeft<=7 ? C.orange : C.yellow)
    : listType==="B" ? C.orange : patient.pct===0 ? C.red : C.yellow;
  return (
    <div style={{ background:hasLog?C.green+"06":C.surface, border:`1px solid ${hasLog?C.green+"28":C.border}`, borderLeft:`3px solid ${hasLog?C.green:urgColor}`, borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:14 }}>
      <div style={{ width:8, height:8, borderRadius:"50%", flexShrink:0, background:hasLog?C.green:urgColor }} />
      <div style={{ flex:"0 0 170px" }}>
        <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{patient.first} {patient.last}</div>
        <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{patient.coach}</div>
      </div>
      <div style={{ flex:"0 0 80px" }}>
        <div style={{ fontSize:12, fontFamily:"'IBM Plex Mono',monospace" }}><span style={{ color:C.muted }}>Day </span><span style={{ color:C.text, fontWeight:700 }}>{patient.days}</span></div>
        {patient.days<=30 && <div style={{ fontSize:10, color:daysLeft<=5?C.red:C.muted, marginTop:2 }}>{daysLeft>0?`${daysLeft}d left`:"closed"}</div>}
      </div>
      <div style={{ flex:"0 0 140px" }}>
        <UsageDots days={patient.days} days4hr={patient.days4hr} />
        <div style={{ fontSize:10, color:C.muted, marginTop:4 }}>{patient.days4hr}/{Math.min(patient.days,30)} ≥4hr ({useRate}%)</div>
      </div>
      <div style={{ flex:"0 0 120px", fontSize:11 }}>
        {patient.phone ? <span style={{ color:C.muted, fontFamily:"'IBM Plex Mono',monospace" }}>{patient.phone.replace(/(\d{3})(\d{3})(\d{4})/,"$1-$2-$3")}</span>
          : <span style={{ color:C.red, fontWeight:700 }}>⚠ No phone</span>}
      </div>
      <div style={{ flex:"0 0 90px" }}>
        {hasLog ? <Pill label="CONTACTED" color={C.green} />
          : listType==="A"&&patient.days4hr===0 ? <Pill label="CRITICAL" color={C.red} />
          : listType==="A" ? <Pill label="NON-COMP" color={C.orange} />
          : <Pill label={`${patient.pct}%`} color={patient.pct<50?C.red:C.orange} />}
      </div>
      <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
        <a href={`${AIRVIEW_BASE}/${patient.ecoId || patient.airviewId}`} target="_blank" rel="noreferrer"
          style={{ background:C.teal+"12", border:`1px solid ${C.teal}28`, color:C.teal, borderRadius:6, padding:"6px 10px", fontSize:11, fontWeight:700, textDecoration:"none" }}>AirView ↗</a>
        <button onClick={()=>onContact(patient)} style={{
          background:hasLog?C.green+"12":C.teal+"12", border:`1px solid ${hasLog?C.green+"28":C.teal+"28"}`,
          color:hasLog?C.green:C.teal, borderRadius:6, padding:"6px 14px",
          fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
        }}>{hasLog?"✓ Logged":"Contact →"}</button>
      </div>
    </div>
  );
}

function ListTab({ title, subtitle, data, listType, color, icon, log, onContact }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(() => {
    let d = data;
    if (search) { const q=search.toLowerCase(); d=d.filter(p=>`${p.first} ${p.last}`.toLowerCase().includes(q)||(p.phone||"").includes(q)); }
    if (filter==="contacted")   d=d.filter(p=> log[p.id]?.length>0);
    if (filter==="uncontacted") d=d.filter(p=>!log[p.id]?.length);
    if (filter==="no_phone")    d=d.filter(p=>!p.phone);
    return d;
  }, [data, search, filter, log]);
  const contactedCount = data.filter(p=>log[p.id]?.length>0).length;
  return (
    <div>
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderTop:`3px solid ${color}`, borderRadius:"12px 12px 0 0", padding:"16px 20px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:C.text }}>{icon} {title}</div>
            <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>{subtitle}</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:28, fontWeight:800, color, fontFamily:"'IBM Plex Mono',monospace", lineHeight:1 }}>{data.length}</div>
            <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{contactedCount}/{data.length} contacted</div>
            <div style={{ width:100, height:3, background:C.faint, borderRadius:2, marginTop:5, marginLeft:"auto" }}>
              <div style={{ height:"100%", width:`${(contactedCount/Math.max(data.length,1))*100}%`, background:C.green, borderRadius:2, transition:"width 0.3s" }} />
            </div>
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <input placeholder="Search by name or phone…" value={search} onChange={e=>setSearch(e.target.value)} style={{ flex:1, background:C.card, border:`1px solid ${C.border}`, color:C.text, borderRadius:7, padding:"8px 12px", fontSize:12, fontFamily:"inherit", outline:"none" }} />
          <select value={filter} onChange={e=>setFilter(e.target.value)} style={{ background:C.card, border:`1px solid ${C.border}`, color:C.muted, borderRadius:7, padding:"8px 10px", fontSize:12, fontFamily:"'IBM Plex Mono',monospace", outline:"none", cursor:"pointer" }}>
            <option value="all">All</option>
            <option value="uncontacted">Not yet contacted</option>
            <option value="contacted">Already contacted</option>
            <option value="no_phone">No phone on file</option>
          </select>
        </div>
      </div>
      <div style={{ display:"flex", padding:"6px 16px 6px 34px", gap:14, background:C.card, borderLeft:`1px solid ${C.border}`, borderRight:`1px solid ${C.border}`, fontSize:10, color:C.faint, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"'IBM Plex Mono',monospace" }}>
        <div style={{ flex:"0 0 170px" }}>Patient</div><div style={{ flex:"0 0 80px" }}>Day</div>
        <div style={{ flex:"0 0 140px" }}>Usage</div><div style={{ flex:"0 0 120px" }}>Phone</div><div style={{ flex:"0 0 90px" }}>Status</div>
      </div>
      <div style={{ border:`1px solid ${C.border}`, borderTop:"none", borderRadius:"0 0 12px 12px", background:C.card, maxHeight:540, overflowY:"auto" }}>
        {filtered.length===0 ? <div style={{ textAlign:"center", color:C.faint, padding:40, fontSize:13 }}>No patients match</div>
          : <div style={{ display:"flex", flexDirection:"column", gap:4, padding:8 }}>
              {filtered.map(p=><PatientCard key={p.id} patient={p} listType={listType} onContact={onContact} hasLog={log[p.id]?.length>0} />)}
            </div>}
      </div>
    </div>
  );
}

export { PatientCard, ListTab };
